import {ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Tool types used by the Data Capture agent.
 *
 * Keep in sync with the backend toolkit definition in:
 * - benchling/llm/data_capture_assistant/agent/data_capture_agent.py (get_data_capture_agent_toolkit)
 *
 * Also update input/output display logic in:
 * - client/llm/chat-library/messages/ChatProgress.tsx (getToolInputDisplay, getToolOutputDisplay)
 */
export enum DataCaptureToolType {
  // Core tools
  Think = 'Think',
  Respond = 'Respond',
  ReadTargetDocument = 'ReadTargetDocument',
  // Legacy name for ReadTargetDocument - kept for backwards compatibility with older chats
  ReadDraftDocument = 'ReadDraftDocument',
  ReadBenchlingDocument = 'ReadBenchlingDocument',
  AskQuestion = 'AskQuestion',
  IdsToModelsTool = 'IdsToModelsTool',
  InstantiateDocument = 'InstantiateDocument',
  SearchEntriesAndTemplates = 'SearchEntriesAndTemplates',
  SearchBenchlingObjects = 'SearchBenchlingObjects',
  TranscribeFile = 'TranscribeFile',
  ReviewTool = 'ReviewTool',
  EditTableCells = 'EditTableCells',
  UpdateDocument = 'UpdateDocument',
  InsertDocumentContent = 'InsertDocumentContent',
  SearchFiles = 'SearchFiles',
  SearchSchemas = 'SearchSchemas',
  FillTables = 'FillTables',
  ReadFiles = 'ReadFiles',
  CreateLookupConfig = 'CreateLookupConfig',
}

/**
 * Human-readable display names for data capture tools when in progress.
 * These are shown while the tool is running.
 */
export const DATA_CAPTURE_TOOL_DISPLAY_NAMES_IN_PROGRESS: Record<DataCaptureToolType, string> = {
  [DataCaptureToolType.Think]: 'Thinking',
  [DataCaptureToolType.Respond]: 'Responding',
  [DataCaptureToolType.ReadTargetDocument]: 'Reading document',
  [DataCaptureToolType.ReadDraftDocument]: 'Reading document', // Legacy
  [DataCaptureToolType.ReadBenchlingDocument]: 'Reading notebook',
  [DataCaptureToolType.AskQuestion]: 'Asking question',
  [DataCaptureToolType.IdsToModelsTool]: 'Reading database objects',
  [DataCaptureToolType.InstantiateDocument]: 'Instantiating document',
  [DataCaptureToolType.SearchEntriesAndTemplates]: 'Searching entries and templates',
  [DataCaptureToolType.SearchBenchlingObjects]: 'Searching Benchling objects',
  [DataCaptureToolType.TranscribeFile]: 'Transcribing file',
  [DataCaptureToolType.ReviewTool]: 'Reviewing',
  [DataCaptureToolType.EditTableCells]: 'Editing table cells',
  [DataCaptureToolType.UpdateDocument]: 'Updating document',
  [DataCaptureToolType.InsertDocumentContent]: 'Inserting document content',
  [DataCaptureToolType.SearchFiles]: 'Searching file content',
  [DataCaptureToolType.SearchSchemas]: 'Searching schemas',
  [DataCaptureToolType.FillTables]: 'Filling tables',
  [DataCaptureToolType.ReadFiles]: 'Reading files',
  [DataCaptureToolType.CreateLookupConfig]: 'Creating lookup configuration',
};

/**
 * Human-readable display names for data capture tools when completed.
 * These are shown after the tool has finished running.
 */
export const DATA_CAPTURE_TOOL_DISPLAY_NAMES_COMPLETED: Record<DataCaptureToolType, string> = {
  [DataCaptureToolType.Think]: 'Thought',
  [DataCaptureToolType.Respond]: 'Responded',
  [DataCaptureToolType.ReadTargetDocument]: 'Read document',
  [DataCaptureToolType.ReadDraftDocument]: 'Read document', // Legacy
  [DataCaptureToolType.ReadBenchlingDocument]: 'Read notebook',
  [DataCaptureToolType.AskQuestion]: 'Asked question',
  [DataCaptureToolType.IdsToModelsTool]: 'Read database objects',
  [DataCaptureToolType.InstantiateDocument]: 'Instantiated document',
  [DataCaptureToolType.SearchEntriesAndTemplates]: 'Searched entries and templates',
  [DataCaptureToolType.SearchBenchlingObjects]: 'Searched Benchling objects',
  [DataCaptureToolType.TranscribeFile]: 'Transcribed file',
  [DataCaptureToolType.ReviewTool]: 'Reviewed',
  [DataCaptureToolType.EditTableCells]: 'Edited table cells',
  [DataCaptureToolType.UpdateDocument]: 'Updated document',
  [DataCaptureToolType.InsertDocumentContent]: 'Inserted document content',
  [DataCaptureToolType.SearchFiles]: 'Searched file content',
  [DataCaptureToolType.SearchSchemas]: 'Searched schemas',
  [DataCaptureToolType.FillTables]: 'Filled tables',
  [DataCaptureToolType.ReadFiles]: 'Read files',
  [DataCaptureToolType.CreateLookupConfig]: 'Created lookup configuration',
};

/**
 * Icons for data capture tools.
 * Using icons that are available in the BenchlingIcon library.
 */
export const DATA_CAPTURE_TOOL_ICONS: Record<DataCaptureToolType, BenchlingIconName> = {
  [DataCaptureToolType.Think]: 'lightbulb',
  [DataCaptureToolType.Respond]: 'comment',
  [DataCaptureToolType.ReadTargetDocument]: 'file',
  [DataCaptureToolType.ReadDraftDocument]: 'file', // Legacy
  [DataCaptureToolType.ReadBenchlingDocument]: 'file',
  [DataCaptureToolType.AskQuestion]: 'magnifying-glass',
  [DataCaptureToolType.IdsToModelsTool]: 'magnifying-glass',
  [DataCaptureToolType.InstantiateDocument]: 'file',
  [DataCaptureToolType.SearchEntriesAndTemplates]: 'magnifying-glass',
  [DataCaptureToolType.SearchBenchlingObjects]: 'magnifying-glass',
  [DataCaptureToolType.TranscribeFile]: 'file',
  [DataCaptureToolType.ReviewTool]: 'check',
  [DataCaptureToolType.EditTableCells]: 'edit',
  [DataCaptureToolType.UpdateDocument]: 'edit',
  [DataCaptureToolType.InsertDocumentContent]: 'plus',
  [DataCaptureToolType.SearchFiles]: 'magnifying-glass',
  [DataCaptureToolType.SearchSchemas]: 'magnifying-glass',
  [DataCaptureToolType.FillTables]: 'edit',
  [DataCaptureToolType.ReadFiles]: 'file',
  [DataCaptureToolType.CreateLookupConfig]: 'magnifying-glass',
};

/**
 * Default icon for unknown tools
 */
export const UNKNOWN_TOOL_ICON: BenchlingIconName = 'wrench';

/**
 * Tools that should be displayed in the progress UI.
 * We exclude Respond as it is handled specially.
 */
export const DATA_CAPTURE_TOOLS_TO_DISPLAY: Array<DataCaptureToolType> = [
  DataCaptureToolType.Think,
  DataCaptureToolType.ReadTargetDocument,
  DataCaptureToolType.ReadDraftDocument, // Legacy
  DataCaptureToolType.ReadBenchlingDocument,
  DataCaptureToolType.AskQuestion,
  DataCaptureToolType.IdsToModelsTool,
  DataCaptureToolType.InstantiateDocument,
  DataCaptureToolType.SearchEntriesAndTemplates,
  DataCaptureToolType.SearchBenchlingObjects,
  DataCaptureToolType.TranscribeFile,
  DataCaptureToolType.ReviewTool,
  DataCaptureToolType.EditTableCells,
  DataCaptureToolType.UpdateDocument,
  DataCaptureToolType.InsertDocumentContent,
  DataCaptureToolType.SearchFiles,
  DataCaptureToolType.SearchSchemas,
  DataCaptureToolType.FillTables,
  DataCaptureToolType.ReadFiles,
  DataCaptureToolType.CreateLookupConfig,
];

/**
 * Format an external tool name for display by replacing underscores with spaces.
 */
function formatExternalToolName(toolName: string): string {
  return toolName.replace(/_/g, ' ');
}

/**
 * Get the display name for a data capture tool based on its status.
 * Returns present tense for pending tools, past tense for completed tools.
 * For unknown tools not in the enum, shows "Calling {name}" or "Called {name}".
 */
export function getDataCaptureToolDisplayName(toolName: string, status?: ToolCallStatus): string {
  const displayNames =
    status === ToolCallStatus.COMPLETED
      ? DATA_CAPTURE_TOOL_DISPLAY_NAMES_COMPLETED
      : DATA_CAPTURE_TOOL_DISPLAY_NAMES_IN_PROGRESS;

  const displayName = displayNames[toolName as DataCaptureToolType];
  if (displayName) {
    return displayName;
  }

  // For external tools not in the known list, show "Calling/Called {formatted name}"
  const formattedName = formatExternalToolName(toolName);
  return status === ToolCallStatus.COMPLETED ? `Called ${formattedName}` : `Calling ${formattedName}`;
}

/**
 * Get the icon for a data capture tool.
 */
export function getDataCaptureToolIcon(toolName: string): BenchlingIconName {
  if (Object.hasOwn(DATA_CAPTURE_TOOL_ICONS, toolName)) {
    return DATA_CAPTURE_TOOL_ICONS[toolName as DataCaptureToolType];
  }
  return UNKNOWN_TOOL_ICON;
}

/**
 * Check if a tool should be displayed in the progress UI.
 * Returns true for known tools in DATA_CAPTURE_TOOLS_TO_DISPLAY and for external/unknown tools.
 */
export function shouldDisplayDataCaptureTool(toolName: string): boolean {
  // Show known tools
  if (DATA_CAPTURE_TOOLS_TO_DISPLAY.includes(toolName as DataCaptureToolType)) {
    return true;
  }
  // Show external tools (tools not in the DataCaptureToolType enum)
  // These may be dynamically added tools with names not in our known list
  if (!Object.values(DataCaptureToolType).includes(toolName as DataCaptureToolType)) {
    return true;
  }
  return false;
}
